import React from 'react';
import { notifications } from '../../utils';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Icon, TextField } from '@material-ui/core';
import { MedicalSupplyMethod, MedicalSupplyType, MedicalSupplyViewModel } from '../../models/injection';
import { MedicalSupplyMethodSelect, MedicalSupplyTypeSelect } from '../../elements';
import { useTranslation } from 'react-i18next';
import { MedicalSuppliesService } from '../../hooks';
import { extractApiErrorMessage } from '../../utils';

interface MedicalSupplyEditModalProps {
  opened: boolean;
  supply: MedicalSupplyViewModel | null;
  onClose: (saved: boolean) => void;
}

const MedicalSupplyEditModal: React.FC<MedicalSupplyEditModalProps> = ({ opened, supply, onClose }) => {
  const { t } = useTranslation();
  const medicalSuppliesService = new MedicalSuppliesService();

  const [producer, setProducer] = React.useState('');
  const [method, setMethod] = React.useState<MedicalSupplyMethod>(MedicalSupplyMethod.Intermuscular);
  const [registrationNumber, setRegistrationNumber] = React.useState('');
  const [supplyType, setSupplyType] = React.useState<MedicalSupplyType>(MedicalSupplyType.Vaccination);
  const [waitingTime, setWaitingTime] = React.useState(0);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (supply) {
      setProducer(supply.producer || '');
      setMethod(supply.method);
      setRegistrationNumber(supply.registrationNumber || '');
      setSupplyType(supply.supplyType);
      setWaitingTime(supply.waitingTime || 0);
    }
  }, [supply]);

  const validate = (): boolean => {
    if (!producer) {
      notifications.warning(t('f-val-supp-producer'));
      return false;
    }
    if (supplyType === MedicalSupplyType.Antibiotic && waitingTime <= 0) {
      notifications.warning(t('f-val-supp-widthd'));
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!supply || !validate()) return;

    setIsSaving(true);
    medicalSuppliesService
      .updateMedicalSupply(supply.id, { method, producer, registrationNumber, supplyType, waitingTime: +waitingTime })
      .then(() => {
        notifications.success(t('f-supp-edit-ok'));
        onClose(true);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <Dialog open={opened} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>{t('f-supp-edit-title')}</DialogTitle>

      <DialogContent>
        <Grid container spacing={1} style={{ paddingTop: 8 }}>
          <Grid item md={6} xs={12}>
            <TextField
              label={t('f-supplies-producer')}
              variant="outlined"
              size="small"
              fullWidth
              value={producer}
              onChange={(e) => setProducer(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label={t('f-com-reg-num')}
              variant="outlined"
              size="small"
              fullWidth
              value={registrationNumber}
              onChange={(e) => setRegistrationNumber(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <MedicalSupplyMethodSelect value={method} onSupplyMethodChange={(value) => setMethod(value)} />
          </Grid>

          <Grid item xs={12} md={6}>
            <MedicalSupplyTypeSelect value={supplyType} onSupplyTypeChange={(value) => setSupplyType(value)} />
          </Grid>

          {(supplyType === MedicalSupplyType.Antibiotic || supplyType === MedicalSupplyType.Any) && (
            <Grid item xs={12} md={6}>
              <TextField
                label={t('f-supp-create-widthd')}
                variant="outlined"
                type="number"
                size="small"
                fullWidth
                value={waitingTime}
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  if (newValue >= 0) setWaitingTime(newValue);
                }}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => onClose(false)} variant="outlined" startIcon={<Icon>close</Icon>}>
          {t('f-com-cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          variant="contained"
          color="primary"
          startIcon={<Icon>save</Icon>}
        >
          {t('f-com-save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MedicalSupplyEditModal;
